import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import Notification from "../../component/notification";
import { FaEdit, FaTrash, FaEye } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import { getDoc } from "firebase/firestore";

const Responses = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const course = location.state?.course;
  const dayId = location.state?.dayId; // Numeric dayId (e.g., 1, 2, 3)

  const [assignments, setAssignments] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "", visible: false });
  const [showModal, setShowModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [actualDayDocId, setActualDayDocId] = useState(null); // Store the actual document ID
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);

  // Helper function to find the actual day document ID
  const findDayDocumentId = async (courseId, targetDayNumber) => {
    try {
      const dayCollectionRef = collection(db, "courses", courseId, "day");
      const daySnapshot = await getDocs(dayCollectionRef);
      
      // Look for document IDs that match the pattern (day1, day2, etc. or just 1, 2, etc.)
      const dayDocs = daySnapshot.docs.map(doc => ({
        id: doc.id,
        numericValue: extractNumericValue(doc.id)
      }));
      
      // Find the document that matches our target day number
      const matchingDoc = dayDocs.find(doc => doc.numericValue === targetDayNumber);
      return matchingDoc ? matchingDoc.id : `${targetDayNumber}`; // Fallback to numeric string
    } catch (error) {
      console.error("Error finding day document ID:", error);
      return `day${targetDayNumber}`; // Fallback to day prefix format
    }
  };

  // Helper function to extract numeric value from document ID
  const extractNumericValue = (docId) => {
    // Handle formats like "day1", "day2" or just "1", "2"
    const match = docId.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  useEffect(() => {
    if (!course?.id || !dayId || isNaN(dayId)) {
      setNotification({ message: "Invalid course or day.", type: "danger", visible: true });
      return;
    }

    const initializeAndFetchAssignments = async () => {
      try {
        // First, find the actual document ID for this day
        const actualDocId = await findDayDocumentId(course.id, dayId);
        setActualDayDocId(actualDocId);

        // Then fetch assignments using the actual document ID
        const assignmentsRef = collection(db, "courses", course.id, "day", actualDocId, "assignments");
        const snapshot = await getDocs(assignmentsRef);
        setAssignments(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      } catch (error) {
        console.error("Error fetching assignments:", error);
        setNotification({ message: `Failed to fetch assignments: ${error.message}`, type: "danger", visible: true });
      }
    };

    initializeAndFetchAssignments();
  }, [course, dayId]);

  // Pagination logic
  const totalPages = Math.ceil(assignments.length / itemsPerPage);
  const paginatedAssignments = assignments.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleEdit = async (assignment) => {
    if (!actualDayDocId) {
      setNotification({ message: "Day document ID not found.", type: "danger", visible: true });
      return;
    }

    try {
      const assignmentRef = doc(db, "courses", course.id, "day", actualDayDocId, "assignments", assignment.id);
      const assignmentSnap = await getDoc(assignmentRef);
      if (assignmentSnap.exists()) {
        navigate("/editcontent", {
          state: {
            course,
            day: dayId, // Pass numeric dayId for UI consistency
            dayDocId: actualDayDocId, // Pass actual document ID for database operations
            editData: { id: assignment.id, ...assignmentSnap.data() }
          }
        });
      } else {
        setNotification({ message: "Assignment not found.", type: "danger", visible: true });
      }
    } catch (error) {
      console.error("Error fetching assignment for edit:", error);
      setNotification({ message: `Failed to load assignment: ${error.message}`, type: "danger", visible: true });
    }
  };

  const handleDelete = (assignment) => {
    setSelectedAssignment(assignment);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedAssignment || !actualDayDocId) return;
    
    try {
      await deleteDoc(doc(db, "courses", course.id, "day", actualDayDocId, "assignments", selectedAssignment.id));
      setAssignments(prev => prev.filter(a => a.id !== selectedAssignment.id));
      setNotification({ message: `Assignment ${selectedAssignment.id} deleted successfully.`, type: "success", visible: true });
    } catch (error) {
      console.error("Error deleting assignment:", error);
      setNotification({ message: `Failed to delete assignment: ${error.message}`, type: "danger", visible: true });
    }
    setShowModal(false);
    setSelectedAssignment(null);
  };

  const handleView = async (assignment) => {
    if (!actualDayDocId) {
      setNotification({ message: "Day document ID not found.", type: "danger", visible: true });
      return;
    }

    try {
      const assignmentRef = doc(db, "courses", course.id, "day", actualDayDocId, "assignments", assignment.id);
      const assignmentSnap = await getDoc(assignmentRef);
      if (assignmentSnap.exists()) {
        navigate("/editcontent", {
          state: {
            course,
            day: dayId, // Pass numeric dayId for UI consistency
            dayDocId: actualDayDocId, // Pass actual document ID for database operations
            editData: { id: assignment.id, ...assignmentSnap.data() },
            readOnly: true
          }
        });
      } else {
        setNotification({ message: "Assignment not found.", type: "danger", visible: true });
      }
    } catch (error) {
      console.error("Error fetching assignment for view:", error);
      setNotification({ message: `Failed to load assignment: ${error.message}`, type: "danger", visible: true });
    }
  };

  // Auto-hide notifications
  useEffect(() => {
    if (notification.visible) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, visible: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.visible]);

  return (
    <div className="container-fluid py-4">
      <div className="bg-white rounded-3 p-4 shadow-sm border">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold mb-0">
            {course?.title || "Course"} - Day {dayId} Assignments
            {actualDayDocId && actualDayDocId !== `${dayId}` && (
              <small className="text-muted ms-2">({actualDayDocId})</small>
            )}
          </h4>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left-square-fill"></i> Back
          </button>
        </div>
        <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Created Date</th>
                <th>Assignment ID</th>
                <th>Title</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAssignments.length === 0 ? (
                <tr><td colSpan={4}>No assignments found for Day {dayId}.</td></tr> 
              ) : (
                paginatedAssignments.map((assignment, idx) => (
                  <tr key={assignment.id || idx}>
                    <td>{assignment.createdDate ? new Date(assignment.createdDate).toLocaleString() : "N/A"}</td>
                    <td>{assignment.id}</td>
                    <td>{assignment.title}</td>
                    <td>
                      <button className="btn btn-link p-1" title="Edit" onClick={() => handleEdit(assignment)}><FaEdit /></button>
                      <button className="btn btn-link p-1 text-danger" title="Delete" onClick={() => handleDelete(assignment)}><FaTrash /></button>
                      <button className="btn btn-link p-1 text-info" title="View" onClick={() => handleView(assignment)}><FaEye /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Showing {assignments.length === 0 ? 0 : (page - 1) * itemsPerPage + 1} to {Math.min(page * itemsPerPage, assignments.length)} of {assignments.length} entries
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              &lt;
            </button>
            <span className="small">Page {page} of {totalPages || 1}</span>
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages || totalPages === 0}
            >
              &gt;
            </button>
          </div>
        </div>
        {/* Notification and Delete Modal */}
        <Notification
          message={notification.message}
          type={notification.type}
          visible={notification.visible}
          onClose={() => setNotification({ ...notification, visible: false })}
          index={1}
        />
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete assignment {selectedAssignment?.id}?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Responses;